import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  // Verificar si hay una sesión activa al cargar la página
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  // Función de inicio de sesión
  const login = async (username: string, password: string): Promise<boolean> => {
    // Verificar las credenciales (en un entorno real, esto se haría con una API)
    // Por seguridad, las credenciales deberían estar en variables de entorno
    const validUsername = import.meta.env.VITE_ADMIN_USERNAME || 'admin';
    const validPassword = import.meta.env.VITE_ADMIN_PASSWORD || 'wedding2025';
    
    if (username === validUsername && password === validPassword) {
      // Crear un token simple (en producción, usaríamos JWT u otro método seguro)
      const token = btoa(`${username}:${new Date().getTime()}`);
      localStorage.setItem('auth_token', token);
      setIsAuthenticated(true);
      return true;
    }
    
    return false;
  };

  // Función de cierre de sesión
  const logout = () => {
    localStorage.removeItem('auth_token');
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado para usar el contexto de autenticación
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};
